import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { Auth } from './auth.service';
import { SpringPage } from '../../shared/models/page';

export interface BoletaLinea {
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  igv: number;
}

export interface BoletaVenta {
  id: number;
  orderId: number;
  serie: string;
  numero: number;
  numeroCompleto: string;
  fechaEmision: string;
  clienteNombre: string;
  clienteDni?: string;
  clienteTelefono?: string;
  clienteEmail?: string;
  clienteDireccion?: string;
  subtotal: number;
  igv: number;
  total: number;
  estado: string;
  envioCliente: 'PENDIENTE' | 'ENVIADO';
  fechaEnvioCliente?: string;
  lineas?: BoletaLinea[];
  origen?: 'MANUAL' | 'WEB' | null;
}

export interface BoletaResumen {
  year: number;
  month: number;
  cantidadBoletas: number;
  cantidadSinEnviar: number;
  totalEmitido: number;
  totalIgv: number;
}

export interface EmisorConfig {
  id?: number;
  ruc: string;
  razonSocial: string;
  titular?: string;
  eslogan?: string;
  direccion: string;
  instagram?: string;
  website?: string;
  serie: string;
  ultimoNumero?: number;
  autoEmitirAlPagar: boolean;
  igvPorcentaje?: number;
}

export interface OrderBoletaStatus {
  orderId: number;
  tieneBoleta: boolean;
  boletaId?: number;
  numeroCompleto?: string;
  envioCliente?: 'PENDIENTE' | 'ENVIADO';
}

@Injectable({ providedIn: 'root' })
export class BoletaService {
  private apiUrl = `${environment.apiUrl}/boletas`;

  constructor(private http: HttpClient, private auth: Auth) {}

  private headers(): HttpHeaders {
    const token = this.auth.getToken();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  getEmisorConfig(): Observable<EmisorConfig> {
    return this.http.get<EmisorConfig>(`${this.apiUrl}/config/emisor`, { headers: this.headers() });
  }

  updateEmisorConfig(config: EmisorConfig): Observable<EmisorConfig> {
    return this.http.put<EmisorConfig>(`${this.apiUrl}/config/emisor`, config, { headers: this.headers() });
  }

  emitir(orderId: number): Observable<BoletaVenta> {
    return this.http.post<BoletaVenta>(`${this.apiUrl}/emitir/${orderId}`, {}, { headers: this.headers() });
  }

  listar(year: number, month: number, page = 0, size = 10, origen?: string): Observable<SpringPage<BoletaVenta>> {
    let params = new HttpParams()
      .set('year', String(year))
      .set('month', String(month))
      .set('page', String(page))
      .set('size', String(size));
    if (origen) params = params.set('origen', origen);
    return this.http.get<SpringPage<BoletaVenta>>(this.apiUrl, { params, headers: this.headers() });
  }

  misBoletas(): Observable<OrderBoletaStatus[]> {
    return this.http.get<OrderBoletaStatus[]>(`${environment.apiUrl}/orders/my-orders/boletas`, { headers: this.headers() });
  }

  descargarMiBoleta(orderId: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/orders/my-orders/${orderId}/boleta/pdf`, {
      headers: this.headers(),
      responseType: 'blob'
    });
  }

  obtenerPorId(id: number): Observable<BoletaVenta> {
    return this.http.get<BoletaVenta>(`${this.apiUrl}/${id}`, { headers: this.headers() });
  }

  obtenerPorOrden(orderId: number): Observable<BoletaVenta> {
    return this.http.get<BoletaVenta>(`${this.apiUrl}/orden/${orderId}`, { headers: this.headers() });
  }

  resumen(year: number, month: number, origen?: string): Observable<BoletaResumen> {
    let params = new HttpParams().set('year', String(year)).set('month', String(month));
    if (origen) params = params.set('origen', origen);
    return this.http.get<BoletaResumen>(`${this.apiUrl}/resumen`, { params, headers: this.headers() });
  }

  estadosOrdenes(orderIds: number[]): Observable<OrderBoletaStatus[]> {
    if (!orderIds.length) return of([]);
    return this.http.get<OrderBoletaStatus[]>(
      `${this.apiUrl}/estados-ordenes?ids=${orderIds.join(',')}`,
      { headers: this.headers() }
    );
  }

  descargarPdf(boletaId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${boletaId}/pdf`, {
      headers: this.headers(),
      responseType: 'blob'
    });
  }

  enviarAlCliente(boletaId: number, email?: string): Observable<BoletaVenta> {
    const body = email ? { email } : {};
    return this.http.post<BoletaVenta>(`${this.apiUrl}/${boletaId}/enviar`, body, { headers: this.headers() });
  }
}
